/**
 * useRuleDetailStats Hook
 *
 * Fetches detailed analytics for a single rule
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import type { RuleDetailStatsResponse } from '@store/generated/jotaudit';

import { useGetAdminRuleDetailStatsQuery } from '../store/adminApi';
import { useGetRuleDetailStatsQuery } from '../store/api';

interface UseRuleDetailStatsParams {
  /** Rule ID */
  ruleId: string;
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
  /** Period in days */
  days?: number;
  /** Start date (ISO format) */
  dateFrom?: string;
  /** End date (ISO format) */
  dateTo?: string;
  /** Comma-separated provider IDs */
  userIds?: string;
  /** Skip query if ruleId is not provided */
  skip?: boolean;
}

interface UseRuleDetailStatsResult {
  /** Rule detail stats data */
  data: RuleDetailStatsResponse | undefined;
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
  /** Overall stats */
  overall: RuleDetailStatsResponse['overall'] | null;
  /** Trend data points */
  trend: RuleDetailStatsResponse['trend'] | null;
  /** Trend granularity */
  trendGranularity: string | null;
  /** Provider breakdown */
  byProvider: RuleDetailStatsResponse['by_provider'] | null;
  /** Rule content (markdown) */
  ruleBodyMd: string | null;
  /** Rule name */
  ruleName: string | null;
}

export const useRuleDetailStats = (params: UseRuleDetailStatsParams): UseRuleDetailStatsResult => {
  const { ruleId, organizationId, days, dateFrom, dateTo, userIds, skip } = params;

  const filterParams = {
    ...(days !== undefined && { days }),
    ...(dateFrom && { dateFrom }),
    ...(dateTo && { dateTo }),
    ...(userIds && { userIds }),
  };

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminRuleDetailStatsQuery(
    organizationId && !skip && ruleId
      ? {
          ruleId,
          organizationId,
          ...filterParams,
        }
      : skipToken,
  );

  const providerQuery = useGetRuleDetailStatsQuery(
    !organizationId && !skip && ruleId
      ? {
          ruleId,
          ...filterParams,
        }
      : skipToken,
  );

  // Use whichever query is active
  const { data, isLoading, error, refetch } = organizationId ? adminQuery : providerQuery;

  const result = useMemo(() => {
    if (!data) {
      return {
        overall: null,
        trend: null,
        trendGranularity: null,
        byProvider: null,
        ruleBodyMd: null,
        ruleName: null,
      };
    }

    return {
      overall: data.overall || null,
      trend: data.trend || null,
      trendGranularity: data.trend_granularity || null,
      byProvider: data.by_provider || null,
      ruleBodyMd: data.rule_body_md || null,
      ruleName: data.rule_name || null,
    };
  }, [data]);

  // Check if error is a 404 (no data yet)
  const isNoDataError = error && 'status' in error && error.status === 404;
  const errorMessage = isNoDataError
    ? 'no_data_yet'
    : error
      ? 'Failed to load rule detail statistics'
      : null;

  return {
    data,
    isLoading,
    error: errorMessage,
    refetch,
    ...result,
  };
};

export default useRuleDetailStats;

